import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useUserProfile } from "../../contexts/UserProfileContext";

interface Tag {
  id: string;
  name: string;
  slug: string;
  description: string | null;
}

export default function PacklistTagsAdmin() {
  const { profile } = useUserProfile();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTag, setNewTag] = useState({ name: "", slug: "", description: "" });
  const [editMode, setEditMode] = useState<string | null>(null);
  const [editData, setEditData] = useState({
    name: "",
    slug: "",
    description: "",
  });

  useEffect(() => {
    if (profile?.role !== "admin") return;

    const fetchTags = async () => {
      const { data, error } = await supabase
        .from("packlist_tags")
        .select("*")
        .order("name");

      if (error) {
        console.error("Fehler beim Laden der Tags:", error.message);
        return;
      }
      setTags(data || []);
      setLoading(false);
    };

    fetchTags();
  }, [profile]);

  const handleAdd = async () => {
    if (!newTag.name || !newTag.slug) return;

    const { data, error } = await supabase
      .from("packlist_tags")
      .insert([newTag])
      .select();

    if (error) {
      alert("Fehler beim Erstellen: " + error.message);
    } else {
      setTags([...tags, ...(data || [])]);
      setNewTag({ name: "", slug: "", description: "" });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Wirklich löschen?")) return;

    const { error } = await supabase
      .from("packlist_tags")
      .delete()
      .eq("id", id);

    if (error) {
      alert("Fehler beim Löschen: " + error.message);
    } else {
      setTags(tags.filter((t) => t.id !== id));
    }
  };

  const startEdit = (tag: Tag) => {
    setEditMode(tag.id);
    setEditData({
      name: tag.name,
      slug: tag.slug,
      description: tag.description || "",
    });
  };

  const cancelEdit = () => {
    setEditMode(null);
  };

  const saveEdit = async (id: string) => {
    const { error } = await supabase
      .from("packlist_tags")
      .update(editData)
      .eq("id", id);

    if (error) {
      alert("Fehler beim Speichern: " + error.message);
    } else {
      const updated = tags.map((t) =>
        t.id === id ? { ...t, ...editData } : t
      );
      setTags(updated);
      setEditMode(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-bold text-sky-400 mb-6">
        🏷️ Tags verwalten
      </h1>

      {/* Neuer Tag */}
      <div className="bg-[#161b27] border border-[#2a3347] rounded-xl p-4 mb-6">
        <h2 className="font-semibold mb-2 text-slate-100">➕ Neuen Tag hinzufügen</h2>
        <div className="flex flex-col gap-3">
          <input
            type="text"
            placeholder="Name"
            value={newTag.name}
            onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
            className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-2 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <input
            type="text"
            placeholder="Slug (z. B. strand)"
            value={newTag.slug}
            onChange={(e) => setNewTag({ ...newTag, slug: e.target.value })}
            className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-2 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <input
            type="text"
            placeholder="Beschreibung"
            value={newTag.description}
            onChange={(e) =>
              setNewTag({ ...newTag, description: e.target.value })
            }
            className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-2 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <button
            onClick={handleAdd}
            className="bg-sky-500 text-white px-4 py-2 rounded hover:bg-sky-600 transition-colors"
          >
            Tag erstellen
          </button>
        </div>
      </div>

      {/* Bestehende Tags */}
      <h2 className="font-semibold mb-2 text-slate-100">📋 Bestehende Tags</h2>
      {loading ? (
        <p className="text-slate-400">Lade Tags...</p>
      ) : tags.length === 0 ? (
        <p className="text-slate-500">Keine Tags vorhanden.</p>
      ) : (
        <ul className="space-y-3">
          {tags.map((tag) => (
            <li
              key={tag.id}
              className="flex justify-between items-start bg-[#1e2639] border border-[#2a3347] p-3 rounded flex-col sm:flex-row sm:items-center"
            >
              {editMode === tag.id ? (
                <div className="flex flex-col gap-2 w-full sm:flex-row sm:items-center sm:gap-4">
                  <input
                    type="text"
                    className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-1 rounded w-full sm:w-40 outline-none focus:border-sky-400"
                    value={editData.name}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-1 rounded w-full sm:w-40 outline-none focus:border-sky-400"
                    value={editData.slug}
                    onChange={(e) =>
                      setEditData({ ...editData, slug: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    className="border border-[#2a3347] bg-[#0d1117] text-slate-100 p-1 rounded w-full outline-none focus:border-sky-400"
                    value={editData.description}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(tag.id)}
                      className="bg-sky-500 text-white px-3 py-1 rounded hover:bg-sky-600 transition-colors"
                    >
                      Speichern
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-[#1e2639] border border-[#2a3347] text-slate-300 px-4 py-2 rounded hover:bg-[#2a3347] transition-colors"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <div className="font-semibold text-slate-100">{tag.name}</div>
                    <div className="text-sm text-slate-500">{tag.slug}</div>
                    {tag.description && (
                      <div className="text-sm text-slate-400 italic">
                        {tag.description}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2 sm:mt-0">
                    <button
                      onClick={() => startEdit(tag)}
                      className="text-sm bg-amber-500 text-white px-3 py-1 rounded hover:bg-amber-600 transition-colors"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                    >
                      Löschen
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
